'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import ArenaHeader from './components/ArenaHeader';
import ChampionBanner from './components/ChampionBanner';
import HeadToHeadScores from './components/HeadToHeadScores';
import ComplianceGrid from './components/ComplianceGrid';
import BadgeDisplay from './components/BadgeDisplay';
import StreakLeaderboard from './components/StreakLeaderboard';
import { forgeArenaService, type ForgeArenaData } from '@/lib/services/forgeArenaService';

export default function TheForgeArenaPage() {
  const [arenaData, setArenaData] = useState<ForgeArenaData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const data = await forgeArenaService.getArenaData();
      setArenaData(data);
    } catch (err) {
      console.log('Arena fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh on window focus / tab return
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    const handleFocus = () => {
      fetchData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchData]);

  return (
    <AppLayout activeRoute="/the-forge-arena">
      <div className="space-y-4 sm:space-y-6">
        <ArenaHeader />
        <ChampionBanner arenaData={arenaData} loading={loading} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <HeadToHeadScores arenaData={arenaData} loading={loading} />
          <ComplianceGrid arenaData={arenaData} loading={loading} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <BadgeDisplay />
          <StreakLeaderboard arenaData={arenaData} loading={loading} />
        </div>
      </div>
    </AppLayout>
  );
}